<script setup>
import { ref, watch, computed } from "vue";

/* ============================================================
   Props / Emits
============================================================ */
const props = defineProps({
  record: { type: Object, required: true }
});

const emit = defineEmits(["close"]);

/* ============================================================
   State
============================================================ */
const saving = ref(false);
const error = ref("");

// keep original identifier safe
const originalShopDomain = ref("");

// editable form model
const form = ref({
  shop_domain: "",
  cpapikey: "",
  active: false,

  dir: "",

  contract_stock: false,
  contract_shoporder: false,
  contract_shopintent: false,
  contract_backfill: false

});

const syncKeys = computed(() =>
  Object.keys(form.value).filter(k => k.startsWith("contract_"))
);
/* ============================================================
   Hydrate from record
============================================================ */
watch(
  () => props.record,
  (shop) => {
    if (!shop) return;

    originalShopDomain.value = shop.shop_domain;

    form.value.shop_domain = shop.shop_domain;
    form.value.cpapikey = shop.cpapikey;
    form.value.active = !!shop.cfg.active;

    form.value.dir = shop.cfg.dir ?? "";

    form.value.contract_stock = !!shop.cfg.contract.stock;
    form.value.contract_shoporder = !!shop.cfg.contract.shoporder;
    form.value.contract_shopintent = !!shop.cfg.contract.shopintent;
    form.value.contract_backfill = !!shop.cfg.contract.backfill;

  },
  { immediate: true }
);

/* ============================================================
   Helpers
============================================================ */
function apiHeaders(cpKey) {
  return {
    "Content-Type": "application/json",
    "x-api-key": localStorage.getItem("api_key"),
        // ComfortPages shop key
    "Cpkey": cpKey ?? ""
  };
}

async function save() {

  saving.value = true;
  error.value = "";
  form.value.shop = props.record;

  try {
    const isLocal = originalShopDomain.value === "localhost";
    const prot = isLocal ? "http" : "https";
    const domain = `${prot}://${encodeURIComponent(originalShopDomain.value)}`;    
    const res = await fetch(
      `${domain}/api/shop/nodezone/cfg`,
      {
        method: "PATCH",
        headers: apiHeaders(form.value.cpapikey),
        body: JSON.stringify(form.value),
      }
    );
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || "Failed to save sync contract");
    }

    // return full updated record to caller
    emit("close", json);

  } catch (err) {
    error.value = err.message;
  } finally {
    saving.value = false;
  }
}

function cancel() {
  emit("close", false);
}
</script>

<template>
  <div class="sync-contract-editor">

    <!-- SHOP DOMAIN -->
    <div class="editor-field">
      <h2>Domain - {{ form.shop_domain }} - Key - {{ form.cpapikey }}</h2>
      <h4 class="sync-toggle-sub">Asset Key - {{ form.dir }}</h4>
    </div>
    <!-- ACTIVE -->
    <div class="editor-field">
      <label>
        <input type="checkbox" v-model="form.active" />
        Active
      </label>
    </div>
    <div><h3 class="sync-toggle-head">Contract Flags</h3></div>
    <!-- Contract FLAGS -->
    <div class="sync-toggle-wrap">
      <div
        v-for="key in syncKeys"
        :key="key"
        class="sync-toggle"
      >
        <label>
          <input
            type="checkbox"
            v-model="form[key]"
          />
          {{ key.replace("contract_", "").replace("_", " ") }}
        </label>
      </div>
    </div>

    <!-- ACTIONS -->
    <div class="editor-actions">
      <button class="btn btn-warning" @click="cancel">
        Cancel
      </button>
      <button class="btn btn-primary" @click="save" :disabled="saving">
        {{ saving ? "Saving…" : "Save" }}
      </button>
    </div>

    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<style scoped lang="less">

h2{
  margin: 0px;

}

.editor-field {
  margin-bottom: 16px;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 4px;
  }

  input[type="text"] {
    width: 100%;
    padding: 6px 8px;
  }
}

.sync-toggle {
  margin-bottom: 12px;

  label {
    display: flex;
    gap: 10px;
    cursor: pointer;
  }

  input[type="checkbox"] {
    transform: scale(1.2);
  }
}

.editor-actions {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 20px;
}
</style>
